import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, Loader2 } from 'lucide-react';
import { useAlbum } from '../../lib/queries';
import { apiFetch, ApiError } from '../../lib/api';
import { useQueryClient } from '@tanstack/react-query';
import type { Photo } from '@w6/shared';

// Cloud Run har 32MB request body-limit; chunka klient-side innan upload.
// Mål: ~25MB per batch (lämnar marginal för multipart-headers/overhead).
const TARGET_BATCH_BYTES = 25 * 1024 * 1024;
const MAX_FILES_PER_BATCH = 15;
// Backend (busboy) sätter samma gräns. Filer större än detta filtreras bort
// klient-side med tydligt fel istället för att låta Cloud Run returnera 500.
const MAX_FILE_BYTES = 25 * 1024 * 1024;

interface UploadProgress {
  totalFiles: number;
  uploadedFiles: number;
  totalBatches: number;
  currentBatch: number;
  failedFiles: { name: string; reason: string }[];
}

function chunkFiles(files: File[]): File[][] {
  const batches: File[][] = [];
  let current: File[] = [];
  let currentBytes = 0;
  for (const file of files) {
    const wouldExceed =
      currentBytes + file.size > TARGET_BATCH_BYTES ||
      current.length >= MAX_FILES_PER_BATCH;
    if (wouldExceed && current.length > 0) {
      batches.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(file);
    currentBytes += file.size;
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

export default function UploadPhotos() {
  const { albumId } = useParams<{ albumId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: albumData } = useAlbum(albumId);

  const [files, setFiles] = useState<File[]>([]);
  const [description, setDescription] = useState('');
  const [photographer, setPhotographer] = useState('');
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [resultIsError, setResultIsError] = useState(false);

  function handleFilesChange(e: ChangeEvent<HTMLInputElement>) {
    const all = e.target.files ? Array.from(e.target.files) : [];
    const tooLarge = all.filter((f) => f.size > MAX_FILE_BYTES);
    const accepted = all.filter((f) => f.size <= MAX_FILE_BYTES);
    setFiles(accepted);
    setProgress(null);
    if (tooLarge.length > 0) {
      const names = tooLarge.map((f) => `${f.name} (${(f.size / 1024 / 1024).toFixed(1)} MB)`).join(', ');
      setResult(`${tooLarge.length} fil(er) hoppades över för att de är större än 25 MB: ${names}`);
      setResultIsError(true);
    } else {
      setResult(null);
      setResultIsError(false);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!albumId || files.length === 0) return;
    setResult(null);
    setResultIsError(false);

    const batches = chunkFiles(files);
    setProgress({
      totalFiles: files.length,
      uploadedFiles: 0,
      totalBatches: batches.length,
      currentBatch: 0,
      failedFiles: [],
    });

    void (async () => {
      let uploadedCount = 0;
      const failedFiles: { name: string; reason: string }[] = [];

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        if (!batch) continue;
        setProgress((prev) =>
          prev ? { ...prev, currentBatch: i + 1, failedFiles } : prev,
        );
        try {
          const fd = new FormData();
          // Lägg fält FÖRE filer i FormData så busboy fires field-events
          // innan file-events. Server behöver fälten för Firestore-doc:en
          // när varje fil parsas (innan Storage-save triggar thumb-trigger).
          if (description.trim()) fd.append('description', description.trim());
          if (photographer.trim()) fd.append('photographer', photographer.trim());
          batch.forEach((f) => { fd.append('photos', f); });
          const uploaded = await apiFetch<Photo[]>(`/albums/${albumId}/upload`, {
            method: 'POST',
            body: fd,
          });
          uploadedCount += uploaded.length;
          setProgress((prev) =>
            prev ? { ...prev, uploadedFiles: uploadedCount, failedFiles } : prev,
          );
        } catch (err) {
          let reason: string;
          if (err instanceof ApiError) {
            const body = err.body;
            const apiMessage =
              typeof body === 'object' && body !== null && 'error' in body
                ? String(body.error)
                : err.message;
            reason = `HTTP ${String(err.status)}: ${apiMessage}`;
          } else if (err instanceof Error) {
            reason = err.message;
          } else {
            reason = 'Okänt fel';
          }
          for (const f of batch) failedFiles.push({ name: f.name, reason });
          setProgress((prev) =>
            prev ? { ...prev, failedFiles } : prev,
          );
        }
      }

      void qc.invalidateQueries({ queryKey: ['album', albumId] });
      void qc.invalidateQueries({ queryKey: ['albums'] });

      const failed = failedFiles.length;
      if (failed === 0) {
        setResult(`Klart: ${uploadedCount} foton uppladdade. Thumbnails genereras i bakgrunden.`);
        setResultIsError(false);
        setFiles([]);
        setDescription('');
        setPhotographer('');
      } else {
        setResult(`${uploadedCount} av ${files.length} foton uppladdade. ${failed} misslyckades.`);
        setResultIsError(true);
      }
    })();
  }

  const isUploading = progress !== null && progress.currentBatch > 0 && progress.uploadedFiles + progress.failedFiles.length < progress.totalFiles;

  return (
    <div className="max-w-2xl mx-auto">
      <button
        type="button"
        onClick={() => navigate('/admin/album')}
        className="flex items-center gap-2 text-ink-muted mb-4"
      >
        <ArrowLeft size={16} /> Tillbaka
      </button>

      <h1 className="text-3xl font-serif text-primary mb-2">Ladda upp foton</h1>
      {albumData?.album && (
        <p className="text-ink-muted mb-6">
          till album: <strong>{albumData.album.name}</strong>
        </p>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
        <label className="block">
          <span className="block text-sm font-medium mb-1">
            Filer (max 200 st, 25 MB/st, jpg/png/webp)
          </span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={handleFilesChange}
            disabled={isUploading}
            className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary file:text-white hover:file:bg-primary-hover file:cursor-pointer disabled:opacity-50"
          />
          {files.length > 0 && (
            <p className="text-sm text-ink-muted mt-2">
              {files.length} fil(er) valda ({(files.reduce((a, f) => a + f.size, 0) / 1024 / 1024).toFixed(1)} MB,
              chunkas i {chunkFiles(files).length} batch{chunkFiles(files).length === 1 ? '' : 'es'})
            </p>
          )}
        </label>

        <label className="block">
          <span className="block text-sm font-medium mb-1">
            Beskrivning (valfritt, gemensam för alla)
          </span>
          <input
            type="text"
            value={description}
            onChange={(e) => { setDescription(e.target.value); }}
            maxLength={500}
            disabled={isUploading}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-primary focus:outline-none disabled:opacity-50"
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium mb-1">Fotograf (valfritt)</span>
          <input
            type="text"
            value={photographer}
            onChange={(e) => { setPhotographer(e.target.value); }}
            maxLength={120}
            disabled={isUploading}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-primary focus:outline-none disabled:opacity-50"
          />
        </label>

        <button
          type="submit"
          disabled={isUploading || files.length === 0}
          className="bg-primary hover:bg-primary-hover text-white px-6 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
        >
          {isUploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
          Ladda upp
        </button>

        {progress && progress.totalBatches > 0 && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-ink-muted">
              <span>
                Batch {progress.currentBatch} / {progress.totalBatches}
                {' · '}
                {progress.uploadedFiles + progress.failedFiles.length} / {progress.totalFiles} foton
              </span>
              <span>
                {Math.round(
                  ((progress.uploadedFiles + progress.failedFiles.length) / progress.totalFiles) * 100,
                )}
                %
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-primary h-full transition-all"
                style={{
                  width: `${((progress.uploadedFiles + progress.failedFiles.length) / progress.totalFiles) * 100}%`,
                }}
              />
            </div>
            {progress.failedFiles.length > 0 && (
              <details className="text-sm text-red-600">
                <summary className="cursor-pointer">
                  {progress.failedFiles.length} fil(er) misslyckades
                </summary>
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  {progress.failedFiles.slice(0, 10).map((f, idx) => (
                    <li key={idx}>
                      <strong>{f.name}</strong>: {f.reason}
                    </li>
                  ))}
                  {progress.failedFiles.length > 10 && (
                    <li>… och {progress.failedFiles.length - 10} till</li>
                  )}
                </ul>
              </details>
            )}
          </div>
        )}

        {result && (
          <p
            className={`text-sm ${resultIsError ? 'text-red-600' : 'text-primary'}`}
            role={resultIsError ? 'alert' : 'status'}
          >
            {result}
          </p>
        )}
      </form>
    </div>
  );
}
